import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { loggedInApiCall, resolveRequestLocale } from "src/api/axiosClient/userApi";
import { zodParseWithLog } from "src/api/apiErrorLogs";
import { useFoodpilotContext } from "src/context/FoodpilotContext";

import {
    IngredientReferenceIntendedUse,
    IngredientReferenceSource,
    IngredientReferenceUnified,
    IngredientReferenceUnifiedSchema,
    SURE_API_DEGRADED_HEADER,
} from "./types";

export const getAllIngredientReferencesUnifiedKey = "allIngredientReferencesUnified" as const;

const IngredientReferenceUnifiedCollectionSchema = z.object({
    "hydra:member": IngredientReferenceUnifiedSchema.array(),
    "hydra:totalItems": z.number().int().nonnegative(),
});

type Options = {
    /** Free-text search forwarded to both sources. */
    q?: string;
    /** Restrict results to a single source. Defaults to merging both. */
    source?: IngredientReferenceSource | "all";
    intendedUse?: IngredientReferenceIntendedUse | null;
    page?: number;
    itemsPerPage?: number;
    enabled?: boolean;
    usedByIngredients?: boolean;
    cacheTime?: number;
    staleTime?: number;
};

/**
 * Browse / search ingredient references across both sources. The backend merges and sorts
 * results by name, so the picker just consumes them as-is.
 */
export const useGetAllIngredientReferencesUnified = (options?: Options) => {
    const {
        q = "",
        source = "all",
        intendedUse = null,
        page = 1,
        itemsPerPage = 50,
        enabled = true,
        usedByIngredients = false,
        cacheTime,
        staleTime,
    } = options || {};
    const { selectedCompany } = useFoodpilotContext();
    const { i18n } = useTranslation();
    const locale = resolveRequestLocale(undefined, i18n.resolvedLanguage);
    const organizationId = selectedCompany?.id;
    const isEnabled = enabled && organizationId !== undefined;

    const query = useQuery({
        queryKey: [
            getAllIngredientReferencesUnifiedKey,
            organizationId ?? null,
            locale,
            q,
            source,
            intendedUse,
            page,
            itemsPerPage,
            usedByIngredients,
        ],
        enabled: isEnabled,
        cacheTime,
        keepPreviousData: false,
        staleTime,
        queryFn: async () => {
            if (!enabled || organizationId === undefined) {
                throw new Error("Cannot load ingredient references while disabled or without a selected organization");
            }

            const response = await loggedInApiCall.get("/api/ingredient-references-unified", {
                params: {
                    organization_id: organizationId,
                    q: q !== "" ? q : undefined,
                    source,
                    intendedUse: intendedUse ?? undefined,
                    page,
                    itemsPerPage,
                    usedByIngredients: usedByIngredients ? 1 : undefined,
                },
            });
            return response;
        },
        select: (response) => {
            const collection = zodParseWithLog(
                response.data,
                IngredientReferenceUnifiedCollectionSchema,
                getAllIngredientReferencesUnifiedKey,
            );

            return {
                items: collection["hydra:member"],
                totalItems: collection["hydra:totalItems"],
                isDegraded: response.headers[SURE_API_DEGRADED_HEADER] === "1",
            };
        },
    });

    return {
        ...query,
        data: isEnabled ? query.data : undefined,
        ingredientReferences: isEnabled ? (query.data?.items as IngredientReferenceUnified[] | undefined) : undefined,
        totalItems: isEnabled ? query.data?.totalItems : undefined,
        isDegraded: isEnabled ? (query.data?.isDegraded ?? false) : false,
    };
};
